// src/features/TeamChat/components/TeamChat.tsx
import classNames from 'classnames'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import { useParams } from 'react-router'
import { useLocalStorage } from 'react-use'

import { useAuth } from 'app/providers/auth/AuthProvider'
import { LOCALE_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useTimezone } from 'common/hooks/useTimezone'

import { useGetTeamMessagesQuery, useGetTeamSummaryQuery } from 'features/TeamChat/api/teamChat.api'
import type { TeamMember, TeamSummary } from 'features/TeamChat/api/teamChat.types'
import { useChatFocused } from 'features/TeamChat/hooks/useChatFocused'
import { useIncomingDing } from 'features/TeamChat/hooks/useIncomingDing'
import { useRevokeOnForbidden } from 'features/TeamChat/hooks/useRevokeOnForbidden'
import { useTeamChatAccess } from 'features/TeamChat/hooks/useTeamChatAccess'
import { useTeamPrefetch } from 'features/TeamChat/hooks/useTeamPrefetch'
import { useTeamResync } from 'features/TeamChat/hooks/useTeamResync'
import { useWindowFocused } from 'features/TeamChat/hooks/useWindowFocused'
import { useWidgetDrag } from 'features/TeamChat/position/useWidgetDrag'
import { useTeamRealtime } from 'features/TeamChat/realtime/useTeamRealtime'
import styles from 'features/TeamChat/teamChat.module.scss'

import { TeamChatBoundary } from './TeamChatBoundary'
import { TeamDraftsProvider } from './TeamDrafts'
import { TeamLauncher } from './TeamLauncher'
import { TeamPanel } from './TeamPanel'

export const HEARTBEAT_MS = 300_000

type Props = { hidden?: boolean }
type WidgetProps = {
  summary?: TeamSummary
  members: TeamMember[]
  timeZone: string
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const EASE_OUT = [0.22, 1, 0.36, 1] as const

// Both states grow from the bottom-right corner the widget is anchored to.
const popIn = (reduce: boolean) => ({
  initial: reduce ? { opacity: 0 } : { opacity: 0, scale: 0.92, y: 12 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: reduce ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 8 },
  transition: { duration: reduce ? 0.12 : 0.22, ease: EASE_OUT },
})

const TeamWidget = ({ summary, members, timeZone, isOpen, setIsOpen }: WidgetProps) => {
  const [focusTarget, setFocusTarget] = useState<'panel' | 'launcher' | null>(null)
  // What is on screen: the exiting state keeps its size and offset until its exit finishes.
  const [shownOpen, setShownOpen] = useState(isOpen)
  const drag = useWidgetDrag(shownOpen)
  const reduce = Boolean(useReducedMotion())
  const clearFocus = () => setFocusTarget(null)
  // A keyboard click (detail 0) is never the tail of a drag.
  const open = (e: MouseEvent<HTMLButtonElement>) => {
    if (drag.consumeDrag() && e.detail !== 0) return
    setFocusTarget('panel')
    setIsOpen(true)
  }
  const collapse = () => {
    setFocusTarget('launcher')
    setIsOpen(false)
  }
  const dockStyle = {
    '--team-right': `${drag.offset.right}px`,
    '--team-bottom': `${drag.offset.bottom}px`,
    '--team-panel-width': `${drag.panelSize.width}px`,
    '--team-panel-height': `${drag.panelSize.height}px`,
  } as CSSProperties

  return (
    <div
      className={classNames(styles.dock, { [styles.dragging]: drag.isDragging })}
      style={dockStyle}>
      <AnimatePresence mode='wait' initial={false} onExitComplete={() => setShownOpen(isOpen)}>
        <motion.div
          key={isOpen ? 'panel' : 'launcher'}
          className={styles.motion}
          {...popIn(reduce)}>
          {isOpen ? (
            <TeamPanel
              summary={summary}
              members={members}
              timeZone={timeZone}
              takeFocus={focusTarget === 'panel'}
              onFocused={clearFocus}
              onCollapse={collapse}
              onDragStart={drag.onPointerDown}
            />
          ) : (
            <TeamLauncher
              openCount={summary?.open_requests ?? 0}
              hasUnread={(summary?.unread_messages ?? 0) > 0}
              takeFocus={focusTarget === 'launcher'}
              onFocused={clearFocus}
              onOpen={open}
              onDragStart={drag.onPointerDown}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  )
}

const TeamChatView = ({ hidden }: Props) => {
  const { siteId } = useParams()
  const { user } = useAuth()
  const { timeZone } = useTimezone()
  const access = useTeamChatAccess({ siteCode: siteId, isLoggedIn: Boolean(user) })
  const { isLive } = useTeamRealtime({
    enabled: access.enabled,
    doctorId: access.doctorId,
    meId: access.me?.id,
  })
  useTeamResync({ enabled: access.enabled, isLive })
  useTeamPrefetch(access.enabled)
  // While live, a slow heartbeat still notices an expired session or revoked access.
  const { data: summary, error } = useGetTeamSummaryQuery(undefined, {
    skip: !access.enabled,
    pollingInterval: isLive ? HEARTBEAT_MS : 0,
  })
  useRevokeOnForbidden(error)
  // Open state lives here, above the hide on Home; the ding is muted only while the user is in the open panel.
  const [isOpen, setIsOpen] = useLocalStorage<boolean>(LOCALE_STORAGE_KEYS.TEAM_CHAT_OPEN, false)
  const focused = useWindowFocused()
  const inChat = useChatFocused()
  const { data: messages } = useGetTeamMessagesQuery(undefined, { skip: !access.enabled })

  const show = access.enabled && !hidden && Boolean(timeZone)
  useIncomingDing({ messages: messages?.messages, quiet: show && Boolean(isOpen) && focused && inChat })
  // Drafts outlive the hide on Home but never carry over to another team.
  return (
    <TeamDraftsProvider key={access.doctorId ?? 0}>
      {show && timeZone && (
        <TeamWidget
          summary={summary}
          members={access.members}
          timeZone={timeZone}
          isOpen={Boolean(isOpen)}
          setIsOpen={setIsOpen}
        />
      )}
    </TeamDraftsProvider>
  )
}

export const TeamChat = (props: Props) => (
  <TeamChatBoundary>
    <TeamChatView {...props} />
  </TeamChatBoundary>
)
